import {
  Case,
  User,
  Category,
  Chat,
  Message,
  CaseDetailResponse,
  Report,
  Tag,
  BindMessagesResponse,
  BooleanResponse,
  CreateCaseResponse,
  MessageTemplate,
  GetMessagesResponse,
  CaseFolderRequest,
  CaseStatusRequest,
  CaseAssigneeRequest,
  CaseCategoryRequest,
  CasePublishedLinkRequest,
  Role,
  Notification,
} from "./classes";
import { logout } from "src/auth/auth";
import queryString from "query-string";

export class LoginRequest {
  email: string;
  password: string;
}
export class BasicRequest {
  id?: string;
  token?: string;
}
export class CreateCaseRequest extends BasicRequest {
  title: string;
  category_id: number;
  folder: string;
  tags: number[];
  assigned_user_id: number;
}
export class GetCaseRequest extends BasicRequest {
  case_id: number;
}
export class CreateCategoryRequest {
  title: string;
}
export class EditCategoryRequest {
  title: string;
  category_id: number;
}
export class DeleteCategoryRequest {
  category_id: number;
}
export class DeleteUserRequest {
  user_id: number;
}
export class SetUserStatusRequest {
  user_id: number;
  status: "active" | "passive";
}
export class UpdateUserRequest {
  user_id: number;
  email?: string;
  name?: string;
  password?: string;
  role_id?: number;
}
export class UpdateProfileRequest {
  email?: string;
  name?: string;
  account_picture?: string;
}
export class AddUserRequest {
  email: string;
  name: string;
  password: string;
  role_id: number;
}
export class CreateMailListUserRequest {
  email?: string;
  name?: string;
}
export class CreateTagRequest {
  title: string;
}
export class EditTagRequest {
  title: string;
  tag_id: number;
}
export class DeleteTagRequest {
  tag_id: number;
}
export class GetTagCasesReq {
  tag_ids: number[];
}
class HeatData {
  date: string;
  count: number;
}
export class ActivityHeatmap {
  activities: HeatData[];
  evidences: HeatData[];
}
export class GetCasesRequest {
  after?: string;
}
export class MessageAttachmentRequest extends BasicRequest {
  sender_id: string;
}
export class BindMessagesRequest extends BasicRequest {
  case_id: number;
  messageIds: number[];
}
export class SendMessageRequest extends BasicRequest {
  source: string;
  recipient_id: string;
  sender_id: string;
  external_message_id: string;
  account_name: string;
  account_picture: string;
  text: string;
}
export class CreateMessageTemplateRequest extends BasicRequest {
  text: string;
}
export class DeleteMessageTemplateRequest extends BasicRequest {
  template_id: number;
}
export class AddActivityRequest extends BasicRequest {
  text: string;
  case_id: number;
}
export class DeleteActivityRequest extends BasicRequest {
  activity_id: number;
  case_id: number;
}
export class AddLinkRequest extends BasicRequest {
  url: string;
  case_id: number;
}
export class DeleteLinkRequest extends BasicRequest {
  link_id: number;
  case_id: number;
}
export class GetMessagesRequest extends BasicRequest {
  oldestMessageId?: number;
  newestMessageId?: number;
  sender_id?: string;
}
export class GetMessagesFromRequest extends BasicRequest {
  sender_id?: string;
}
export class CreateReportRequest extends BasicRequest {
  text: string;
  case_id: number;
  source: string;
  account_name: string;
  phone: string;
  created_at: string;
  file_ids: number[];
}
export class AddEvidenceRequest extends BasicRequest {
  case_id: number;
  text: string;
  file_ids?: number[];
}
export class DeleteEvidenceRequest extends BasicRequest {
  case_id: number;
  evidence_id: number;
}
export class AddImageRequest extends BasicRequest {
  case_id: number;
  file_id: number;
}
export class EditCaseTagsRequest {
  deletedTags: number[];
  insertedTags: number[];
  case_id: number;
}
export class SetCaseRequest {
  case_id: number;
  title?: string;
}
export class FacebookLoginRequest {
  social_id: string;
  token: string;
}
export class DubitoAPI {
  public host =
    !process.env.NODE_ENV || process.env.NODE_ENV === "development"
      ? "https://api.dubito.co"
      : "https://api.dubito.co";

  getAuthHeader() {
    const id = window.localStorage.getItem("id");
    const token = window.localStorage.getItem("token");
    const _headers = new Headers();
    _headers.append("Authorization", id + ":" + token);
    return _headers;
  }
  async fetchUrl(endpoint, _init) {
    const _req = new Request(`${this.host}` + endpoint, _init);
    try {
      const resp = await fetch(_req);
      if (resp.status === 401) {
        logout();
        return Promise.reject(resp.json);
      }
      return resp.json();
    } catch (err) {
      return Promise.reject(err);
    }
  }

  async login(req: LoginRequest): Promise<JSON> {
    const _headers = new Headers();
    _headers.append("Content-Type", "application/json;charset=utf-8");
    const _init = {
      method: "POST",
      headers: _headers,
      body: JSON.stringify(req),
    } as RequestInit;

    const _req = new Request(`${this.host}/login`, _init);
    try {
      const resp = await fetch(_req);
      return resp.json();
    } catch (err) {
      return Promise.reject(err);
    }
  }
  async getCases(req?: GetCasesRequest): Promise<Case[]> {
    const _headers = this.getAuthHeader();
    _headers.append("Content-Type", "application/json;charset=utf-8");
    let url = "/cases";
    if (req && req.after) {
      url = `/cases?after=${encodeURIComponent(req.after)}`;
    }
    const _init = {
      method: "GET",
      headers: _headers,
    } as RequestInit;
    return this.fetchUrl(url, _init);
  }
  async getTagCases(req: GetTagCasesReq): Promise<number[]> {
    let query = "?";

    for (const id in req.tag_ids) {
      if (query.length > 1) {
        query += "&";
      }
      query += `tag=${encodeURIComponent(req.tag_ids[id] + "")}`;
    }
    if (query.length === 1) {
      query = "";
    }
    const _headers = this.getAuthHeader();
    _headers.append("Content-Type", "application/json;charset=utf-8");
    const _init = {
      method: "GET",
      headers: _headers,
    } as RequestInit;
    return this.fetchUrl("/cases" + query, _init);
  }
  async getUsers(req?: BasicRequest): Promise<User[]> {
    const _headers = this.getAuthHeader();
    _headers.append("Content-Type", "application/json;charset=utf-8");
    const _init = {
      method: "GET",
      headers: _headers,
    } as RequestInit;
    return this.fetchUrl("/users", _init);
  }
  async deleteUser(req?: DeleteUserRequest): Promise<BooleanResponse> {
    const _headers = this.getAuthHeader();
    _headers.append("Content-Type", "application/json;charset=utf-8");
    const _init = {
      method: "DELETE",
      headers: _headers,
      body: JSON.stringify(req),
    } as RequestInit;
    return this.fetchUrl(
      `/users/${encodeURIComponent(req.user_id + "")}`,
      _init
    );
  }
  async getNotifications(req?: BasicRequest): Promise<Notification[]> {
    const _headers = this.getAuthHeader();
    _headers.append("Content-Type", "application/json;charset=utf-8");
    const _init = {
      method: "GET",
      headers: _headers,
    } as RequestInit;
    return this.fetchUrl("/notifications", _init);
  }
  async markNotificationsAsRead(): Promise<any> {
    const _headers = this.getAuthHeader();
    _headers.append("Content-Type", "application/json;charset=utf-8");
    const _init = {
      method: "POST",
      headers: _headers,
    } as RequestInit;
    return this.fetchUrl(`/notifications/read`, _init);
  }
  async setUserStatus(req?: SetUserStatusRequest): Promise<BooleanResponse> {
    const _headers = this.getAuthHeader();
    _headers.append("Content-Type", "application/json;charset=utf-8");
    const _init = {
      method: "POST",
      headers: _headers,
      body: JSON.stringify(req),
    } as RequestInit;
    return this.fetchUrl(
      `/users/${encodeURIComponent(req.user_id + "")}/status`,
      _init
    );
  }
  async updateUser(req?: UpdateUserRequest): Promise<BooleanResponse> {
    const _headers = this.getAuthHeader();
    _headers.append("Content-Type", "application/json;charset=utf-8");
    const _init = {
      method: "PUT",
      headers: _headers,
      body: JSON.stringify(req),
    } as RequestInit;
    return this.fetchUrl(
      `/users/${encodeURIComponent(req.user_id + "")}`,
      _init
    );
  }
  async updateProfile(req?: UpdateProfileRequest): Promise<BooleanResponse> {
    const _headers = this.getAuthHeader();
    _headers.append("Content-Type", "application/json;charset=utf-8");
    const _init = {
      method: "PUT",
      headers: _headers,
      body: JSON.stringify(req),
    } as RequestInit;
    return this.fetchUrl(`/profile`, _init);
  }
  async addUser(req?: AddUserRequest): Promise<BooleanResponse> {
    const _headers = this.getAuthHeader();
    _headers.append("Content-Type", "application/json;charset=utf-8");
    const _init = {
      method: "POST",
      headers: _headers,
      body: JSON.stringify(req),
    } as RequestInit;
    return this.fetchUrl("/users", _init);
  }
  async getMailList(): Promise<User[]> {
    const _headers = this.getAuthHeader();
    _headers.append("Content-Type", "application/json;charset=utf-8");
    const _init = {
      method: "GET",
      headers: _headers,
    } as RequestInit;
    return this.fetchUrl("/mailList", _init);
  }
  async deleteMailList(req?: DeleteUserRequest): Promise<BooleanResponse> {
    const _headers = this.getAuthHeader();
    _headers.append("Content-Type", "application/json;charset=utf-8");
    const _init = {
      method: "DELETE",
      headers: _headers,
    } as RequestInit;
    return this.fetchUrl(
      `/mailList/${encodeURIComponent(req.user_id + "")}`,
      _init
    );
  }
  async updateMailList(req?: UpdateUserRequest): Promise<BooleanResponse> {
    const _headers = this.getAuthHeader();
    _headers.append("Content-Type", "application/json;charset=utf-8");
    const _init = {
      method: "PUT",
      headers: _headers,
      body: JSON.stringify(req),
    } as RequestInit;
    return this.fetchUrl(
      `/mailList/${encodeURIComponent(req.user_id + "")}`,
      _init
    );
  }
  async addToMailList(
    req?: CreateMailListUserRequest
  ): Promise<BooleanResponse> {
    const _headers = this.getAuthHeader();
    _headers.append("Content-Type", "application/json;charset=utf-8");
    const _init = {
      method: "POST",
      headers: _headers,
      body: JSON.stringify(req),
    } as RequestInit;
    return this.fetchUrl("/mailList", _init);
  }
  async getRoles(): Promise<Role[]> {
    const _headers = this.getAuthHeader();
    _headers.append("Content-Type", "application/json;charset=utf-8");
    const _init = {
      method: "GET",
      headers: _headers,
    } as RequestInit;
    return this.fetchUrl("/roles", _init);
  }
  async getCategories(): Promise<Category[]> {
    const _headers = this.getAuthHeader();
    _headers.append("Content-Type", "application/json;charset=utf-8");
    const _init = {
      method: "GET",
      headers: _headers,
    } as RequestInit;
    return this.fetchUrl("/categories", _init);
  }
  async getTags(req?: BasicRequest): Promise<Tag[]> {
    const _headers = this.getAuthHeader();
    _headers.append("Content-Type", "application/json;charset=utf-8");
    const _init = {
      method: "GET",
      headers: _headers,
    } as RequestInit;
    return this.fetchUrl("/tags", _init);
  }
  async getReports(): Promise<Report[]> {
    const _headers = this.getAuthHeader();
    _headers.append("Content-Type", "application/json;charset=utf-8");
    const _init = {
      method: "GET",
      headers: _headers,
    } as RequestInit;
    return this.fetchUrl("/reports", _init);
  }
  async getActivityHeatmap(user_id: string): Promise<ActivityHeatmap> {
    const _headers = this.getAuthHeader();
    _headers.append("Content-Type", "application/json;charset=utf-8");
    const _init = {
      method: "GET",
      headers: _headers,
    } as RequestInit;
    return this.fetchUrl(`/profile/heatmap/${user_id}`, _init);
  }
  async getActivityForDate(
    user_id: string,
    date: string
  ): Promise<ActivityHeatmap> {
    const _headers = this.getAuthHeader();
    _headers.append("Content-Type", "application/json;charset=utf-8");
    const _init = {
      method: "GET",
      headers: _headers,
    } as RequestInit;
    return this.fetchUrl(`/profile/heatmap/${user_id}/${date}`, _init);
  }
  async getMessages(req?: GetMessagesRequest): Promise<GetMessagesResponse> {
    let q = queryString.stringify(req);
    if (q.length > 0) {
      q = "?" + q;
    }
    //console.log(req);
    const _headers = this.getAuthHeader();
    _headers.append("Content-Type", "application/json;charset=utf-8");
    const _init = {
      method: "GET",
      headers: _headers,
    } as RequestInit;
    return this.fetchUrl("/messages" + q, _init);
  }
  async getSocialLogins(): Promise<{
    success: boolean;
    facebook?: {
      provider: string;
      expire_at: string;
    };
  }> {
    const _headers = this.getAuthHeader();
    _headers.append("Content-Type", "application/json;charset=utf-8");
    const _init = {
      method: "GET",
      headers: _headers,
    } as RequestInit;
    return this.fetchUrl(`/auth/social`, _init);
  }
  async facebookLogin(req: FacebookLoginRequest): Promise<ActivityHeatmap> {
    const _headers = this.getAuthHeader();
    _headers.append("Content-Type", "application/json;charset=utf-8");
    const _init = {
      method: "POST",
      headers: _headers,
      body: JSON.stringify(req),
    } as RequestInit;
    return this.fetchUrl(`/auth/social/facebook`, _init);
  }
  async getCase(req?: GetCaseRequest): Promise<CaseDetailResponse> {
    const _headers = this.getAuthHeader();
    _headers.append("Content-Type", "application/json;charset=utf-8");
    const _init = {
      method: "GET",
      headers: _headers,
    } as RequestInit;
    return this.fetchUrl(
      `/cases/${encodeURIComponent(req.case_id + "")}`,
      _init
    );
  }
  async getEvidenceAttachments(
    req?: GetCaseRequest
  ): Promise<CaseDetailResponse> {
    const _headers = this.getAuthHeader();
    _headers.append("Content-Type", "application/json;charset=utf-8");
    const _init = {
      method: "GET",
      headers: _headers,
    } as RequestInit;
    return this.fetchUrl(
      `/cases/${encodeURIComponent(req.case_id + "")}/evidenceAttachments`,
      _init
    );
  }
  async getReportAttachments(
    req?: GetCaseRequest
  ): Promise<CaseDetailResponse> {
    const _headers = this.getAuthHeader();
    _headers.append("Content-Type", "application/json;charset=utf-8");
    const _init = {
      method: "GET",
      headers: _headers,
    } as RequestInit;
    return this.fetchUrl(
      `/cases/${encodeURIComponent(req.case_id + "")}/reportAttachments`,
      _init
    );
  }
  async getMessageAttachments(
    req?: MessageAttachmentRequest
  ): Promise<CaseDetailResponse> {
    const _headers = this.getAuthHeader();
    _headers.append("Content-Type", "application/json;charset=utf-8");
    const _init = {
      method: "GET",
      headers: _headers,
    } as RequestInit;
    return this.fetchUrl(
      `/chats/${encodeURIComponent(req.sender_id)}/attachments`,
      _init
    );
  }
  async bindMessages(req?: BindMessagesRequest): Promise<BindMessagesResponse> {
    const _headers = this.getAuthHeader();
    _headers.append("Content-Type", "application/json;charset=utf-8");
    const _init = {
      method: "POST",
      headers: _headers,
      body: JSON.stringify(req),
    } as RequestInit;
    return this.fetchUrl(
      `/cases/${encodeURIComponent(req.case_id + "")}/bindMessages`,
      _init
    );
  }
  async sendMessage(req?: SendMessageRequest): Promise<BooleanResponse> {
    const _headers = this.getAuthHeader();
    _headers.append("Content-Type", "application/json;charset=utf-8");
    const _init = {
      method: "POST",
      headers: _headers,
      body: JSON.stringify(req),
    } as RequestInit;
    return this.fetchUrl("/chats", _init);
  }
  async createCase(req?: CreateCaseRequest): Promise<CreateCaseResponse> {
    const _headers = this.getAuthHeader();
    _headers.append("Content-Type", "application/json;charset=utf-8");
    const _init = {
      method: "POST",
      headers: _headers,
      body: JSON.stringify(req),
    } as RequestInit;
    return this.fetchUrl("/cases", _init);
  }
  async getMessageTemplates(): Promise<MessageTemplate[]> {
    const _headers = this.getAuthHeader();
    _headers.append("Content-Type", "application/json;charset=utf-8");
    const _init = {
      method: "GET",
      headers: _headers,
    } as RequestInit;
    return this.fetchUrl("/messagetemplates", _init);
  }
  async createMessageTemplate(
    req?: CreateMessageTemplateRequest
  ): Promise<MessageTemplate[]> {
    const _headers = this.getAuthHeader();
    _headers.append("Content-Type", "application/json;charset=utf-8");
    const _init = {
      method: "POST",
      headers: _headers,
      body: JSON.stringify(req),
    } as RequestInit;
    return this.fetchUrl("/messagetemplates", _init);
  }
  async deleteMessageTemplate(
    req?: DeleteMessageTemplateRequest
  ): Promise<MessageTemplate[]> {
    const _headers = this.getAuthHeader();
    _headers.append("Content-Type", "application/json;charset=utf-8");
    const _init = {
      method: "DELETE",
      headers: _headers,
      body: JSON.stringify(req),
    } as RequestInit;
    return this.fetchUrl(
      `/messagetemplates/${encodeURIComponent(req.template_id + "")}`,
      _init
    );
  }
  async markMessagesAsRead(req?: MessageAttachmentRequest): Promise<any> {
    const _headers = this.getAuthHeader();
    _headers.append("Content-Type", "application/json;charset=utf-8");
    const _init = {
      method: "PUT",
      headers: _headers,
      body: JSON.stringify(req),
    } as RequestInit;
    return this.fetchUrl(
      `/chats/${encodeURIComponent(req.sender_id)}/markAsRead`,
      _init
    );
  }

  async addActivity(req?: AddActivityRequest): Promise<BooleanResponse> {
    req.id = window.localStorage.getItem("id");
    req.token = window.localStorage.getItem("token");

    const _headers = this.getAuthHeader();
    _headers.append("Content-Type", "application/json;charset=utf-8");
    const _init = {
      method: "POST",
      headers: _headers,
      body: JSON.stringify(req),
    } as RequestInit;
    return this.fetchUrl(
      `/cases/${encodeURIComponent(req.case_id + "")}/activity`,
      _init
    );
  }
  async deleteActivity(req?: DeleteActivityRequest): Promise<BooleanResponse> {
    const _headers = this.getAuthHeader();
    _headers.append("Content-Type", "application/json;charset=utf-8");
    const _init = {
      method: "DELETE",
      headers: _headers,
      body: JSON.stringify(req),
    } as RequestInit;
    return this.fetchUrl(
      `/cases/${encodeURIComponent(
        req.case_id + ""
      )}/activity/${encodeURIComponent(req.activity_id + "")}`,
      _init
    );
  }
  async addLink(req?: AddLinkRequest): Promise<BooleanResponse> {
    const _headers = this.getAuthHeader();
    _headers.append("Content-Type", "application/json;charset=utf-8");
    const _init = {
      method: "POST",
      headers: _headers,
      body: JSON.stringify(req),
    } as RequestInit;
    return this.fetchUrl("/links", _init);
  }
  async deleteLink(req?: DeleteLinkRequest): Promise<BooleanResponse> {
    const _headers = this.getAuthHeader();
    _headers.append("Content-Type", "application/json;charset=utf-8");
    const _init = {
      method: "DELETE",
      headers: _headers,
      body: JSON.stringify(req),
    } as RequestInit;
    return this.fetchUrl(
      `/links/${encodeURIComponent(req.link_id + "")}`,
      _init
    );
  }
  async addEvidence(req?: AddEvidenceRequest): Promise<BooleanResponse> {
    req.id = window.localStorage.getItem("id");
    req.token = window.localStorage.getItem("token");

    const _headers = this.getAuthHeader();
    _headers.append("Content-Type", "application/json;charset=utf-8");
    const _init = {
      method: "POST",
      headers: _headers,
      body: JSON.stringify(req),
    } as RequestInit;
    return this.fetchUrl(
      `/cases/${encodeURIComponent(req.case_id + "")}/evidence`,
      _init
    );
  }
  async deleteEvidence(req?: DeleteEvidenceRequest): Promise<BooleanResponse> {
    const _headers = this.getAuthHeader();
    _headers.append("Content-Type", "application/json;charset=utf-8");
    const _init = {
      method: "DELETE",
      headers: _headers,
      body: JSON.stringify(req),
    } as RequestInit;
    return this.fetchUrl(
      `/cases/${encodeURIComponent(
        req.case_id + ""
      )}/evidence/${encodeURIComponent(req.evidence_id + "")}`,
      _init
    );
  }
  async addImage(req?: AddImageRequest): Promise<BooleanResponse> {
    const _headers = this.getAuthHeader();
    _headers.append("Content-Type", "application/json;charset=utf-8");
    const _init = {
      method: "POST",
      headers: _headers,
      body: JSON.stringify(req),
    } as RequestInit;
    return this.fetchUrl(
      `/cases/${encodeURIComponent(req.case_id + "")}/image`,
      _init
    );
  }
  async createReport(req?: CreateReportRequest): Promise<CreateCaseResponse> {
    const _headers = this.getAuthHeader();
    _headers.append("Content-Type", "application/json;charset=utf-8");
    const _init = {
      method: "POST",
      headers: _headers,
      body: JSON.stringify(req),
    } as RequestInit;
    return this.fetchUrl("/reports", _init);
  }
  async setCaseFolder(req: CaseFolderRequest): Promise<BooleanResponse> {
    const _headers = this.getAuthHeader();
    _headers.append("Content-Type", "application/json;charset=utf-8");
    const _init = {
      method: "PUT",
      headers: _headers,
      body: JSON.stringify(req),
    } as RequestInit;
    return this.fetchUrl(
      `/cases/${encodeURIComponent(req.case_id + "")}/folder`,
      _init
    );
  }
  async setCaseStatus(req: CaseStatusRequest): Promise<BooleanResponse> {
    const _headers = this.getAuthHeader();
    _headers.append("Content-Type", "application/json;charset=utf-8");
    const _init = {
      method: "PUT",
      headers: _headers,
      body: JSON.stringify(req),
    } as RequestInit;
    return this.fetchUrl(
      `/cases/${encodeURIComponent(req.case_id + "")}/status`,
      _init
    );
  }
  async setCaseAssignee(req: CaseAssigneeRequest): Promise<BooleanResponse> {
    const _headers = this.getAuthHeader();
    _headers.append("Content-Type", "application/json;charset=utf-8");
    const _init = {
      method: "PUT",
      headers: _headers,
      body: JSON.stringify(req),
    } as RequestInit;
    return this.fetchUrl(
      `/cases/${encodeURIComponent(req.case_id + "")}/assignee`,
      _init
    );
  }
  async setCaseCategory(req: CaseCategoryRequest): Promise<BooleanResponse> {
    const _headers = this.getAuthHeader();
    _headers.append("Content-Type", "application/json;charset=utf-8");
    const _init = {
      method: "PUT",
      headers: _headers,
      body: JSON.stringify(req),
    } as RequestInit;
    return this.fetchUrl(
      `/cases/${encodeURIComponent(req.case_id + "")}/category`,
      _init
    );
  }
  async setCaseTitle(req: SetCaseRequest): Promise<BooleanResponse> {
    const _headers = this.getAuthHeader();
    _headers.append("Content-Type", "application/json;charset=utf-8");
    const _init = {
      method: "PUT",
      headers: _headers,
      body: JSON.stringify(req),
    } as RequestInit;
    return this.fetchUrl(
      `/cases/${encodeURIComponent(req.case_id + "")}/title`,
      _init
    );
  }
  async createCategory(req: CreateCategoryRequest): Promise<BooleanResponse> {
    const _headers = this.getAuthHeader();
    _headers.append("Content-Type", "application/json;charset=utf-8");
    const _init = {
      method: "POST",
      headers: _headers,
      body: JSON.stringify(req),
    } as RequestInit;
    return this.fetchUrl("/categories", _init);
  }
  async editCategory(req: EditCategoryRequest): Promise<BooleanResponse> {
    const _headers = this.getAuthHeader();
    _headers.append("Content-Type", "application/json;charset=utf-8");
    const _init = {
      method: "PUT",
      headers: _headers,
      body: JSON.stringify(req),
    } as RequestInit;
    return this.fetchUrl(
      `/categories/${encodeURIComponent(req.category_id + "")}`,
      _init
    );
  }
  async deleteCategory(req: DeleteCategoryRequest): Promise<BooleanResponse> {
    const _headers = this.getAuthHeader();
    _headers.append("Content-Type", "application/json;charset=utf-8");
    const _init = {
      method: "DELETE",
      headers: _headers,
    } as RequestInit;
    return this.fetchUrl(
      `/categories/${encodeURIComponent(req.category_id + "")}`,
      _init
    );
  }
  async createTag(req: CreateCategoryRequest): Promise<BooleanResponse> {
    const _headers = this.getAuthHeader();
    _headers.append("Content-Type", "application/json;charset=utf-8");
    const _init = {
      method: "POST",
      headers: _headers,
      body: JSON.stringify(req),
    } as RequestInit;
    return this.fetchUrl("/tags", _init);
  }
  async editTag(req: EditTagRequest): Promise<BooleanResponse> {
    const _headers = this.getAuthHeader();
    _headers.append("Content-Type", "application/json;charset=utf-8");
    const _init = {
      method: "PUT",
      headers: _headers,
      body: JSON.stringify(req),
    } as RequestInit;
    return this.fetchUrl(`/tags/${encodeURIComponent(req.tag_id + "")}`, _init);
  }
  async deleteTag(req: DeleteTagRequest): Promise<BooleanResponse> {
    const _headers = this.getAuthHeader();
    _headers.append("Content-Type", "application/json;charset=utf-8");
    const _init = {
      method: "DELETE",
      headers: _headers,
      body: JSON.stringify(req),
    } as RequestInit;
    return this.fetchUrl(`/tags/${encodeURIComponent(req.tag_id + "")}`, _init);
  }
  async editCaseTags(req: EditCaseTagsRequest): Promise<BooleanResponse> {
    const _headers = this.getAuthHeader();
    _headers.append("Content-Type", "application/json;charset=utf-8");
    const _init = {
      method: "PUT",
      headers: _headers,
      body: JSON.stringify(req),
    } as RequestInit;
    return this.fetchUrl(
      `/cases/${encodeURIComponent(req.case_id + "")}/tags`,
      _init
    );
  }
  async setCasePublishedLink(
    req: CasePublishedLinkRequest
  ): Promise<BooleanResponse> {
    const _headers = this.getAuthHeader();
    _headers.append("Content-Type", "application/json;charset=utf-8");
    const _init = {
      method: "PUT",
      headers: _headers,
      body: JSON.stringify(req),
    } as RequestInit;
    return this.fetchUrl(
      `/cases/${encodeURIComponent(req.case_id + "")}/publishedLink`,
      _init
    );
  }

  async registerToken(token: string): Promise<BooleanResponse> {
    const _headers = this.getAuthHeader();
    _headers.append("Content-Type", "application/json;charset=utf-8");
    const _init = {
      method: "PUT",
      headers: _headers,
      body: JSON.stringify({ fcmToken: token }),
    } as RequestInit;
    return this.fetchUrl(`/notifications/register`, _init);
  }
  async getUrlVirality(url: string): Promise<any> {
    const _headers = this.getAuthHeader();
    _headers.append("Content-Type", "application/json;charset=utf-8");
    const _init = {
      method: "GET",
      headers: _headers,
    } as RequestInit;
    return this.fetchUrl(`/urls?url=${encodeURIComponent(url)}`, _init);
  }
}



// WEBPACK FOOTER //
// ./src/api/DubitoAPI.ts